import React, { useState, useEffect, useRef } from "react";
import { Form as SitecoreForm } from "@sitecore-jss/sitecore-jss-react-forms";
import { RichText } from "@sitecore-jss/sitecore-jss-react";
import { sitecoreApiKey } from "temp/config";
import {
  InvestorRelationsField,
  OutlinedField,
  ScheduleAVisitField,
} from "components/components/atoms";
import "./form-styles.css";
import styled from "styled-components";
import cx from "classnames";
import {
  useUiValue,
  openSuccessModalPromo,
  openSuccessModalInvestors,
  openSuccessModal,
  openSuccessModalWarranty,
} from "stores/uiContext";
import { WaitingScreen } from "components/components/molecules";
import { track } from "utils/tracking";
import trackingEvents from "utils/trackingEvents";
import { useSitecoreContext } from "stores/sitecoreContext";
import { useHistory, withRouter, useLocation } from "react-router-dom";
import { useCountryValue } from "stores/country";
import { validateEmail } from "utils/validateEmail";
import { validateZipCode } from "utils/validateZipCode";
import pageTypes from "utils/pageTypes";
import useMarketCommunitiesForm from "utils/hooks/useMarketCommunitites";
import useConsoleLogging from "utils/hooks/useConsoleLogging";
import useDynamicsForm from "utils/hooks/useDynamicsForm";
import { Helmet } from "react-helmet";
import { getNameFormTextFormat } from "utils/getNameFormTextFormat";
import { getGAClientId, getGASessionId } from "utils/googleAnalitycsFunctions";
import queryString from "query-string";

const FormContainer = styled.fieldset.attrs({
  className: "flex flex-col items-center justify-center lg:mb-0 mb-12",
})`
  & form {
    max-width: 50rem;
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .form-errors {
    display: none;
  }
`;

const LabelComponentInvestor = (props) => (
  <label
    className={props.field.model.cssClass}
    htmlFor={props.field.valueField.id}
  >
    {props.children}
    <RichText
      tag="span"
      className="richtext text-mattamy-blue font-graphie text-base"
      field={{
        value: props.field.model.title,
      }}
    />
  </label>
);

const LabelComponentPromos = (props) => (
  <label
    className={props.field.model.cssClass}
    htmlFor={props.field.valueField.id}
  >
    {props.children}
    <RichText
      tag="span"
      className="richtext text-action-blue font-graphie text-base"
      field={{ value: props.field.model.title }}
    />
  </label>
);

const getSitecoreFormData = (
  formTitle,
  dynamicsFormFields,
  hasCommunityDropdown = false,
  marketCommunitiesForm = [],
  dynamicsInputFieldsIds,
  utmParams
) => {
  let sitecoreFormData = {};
  if (typeof window !== "undefined") {
    const formDiv = document.getElementById(formTitle || "Form");

    if (formDiv) {
      const firstNameInput = formDiv.querySelectorAll("input[type=text]")[3];
      const firstName = firstNameInput && firstNameInput.value;
      sitecoreFormData.firstName = firstName;

      const lastNameInput = formDiv.querySelectorAll("input[type=text]")[4];
      const lastName = lastNameInput && lastNameInput.value;
      sitecoreFormData.lastName = lastName;

      const emailInput = formDiv.querySelector("input[type=email]");
      const email = emailInput && emailInput.value;
      sitecoreFormData.email = email;

      //dropdown
      const countryInput = hasCommunityDropdown
        ? formDiv.querySelectorAll("select")[1]
        : formDiv.querySelectorAll("select")[0];
      const sCountry = countryInput && countryInput.value;
      sitecoreFormData.country = sCountry;

      const zipCodeInput = formDiv.querySelectorAll("input[type=text]")[5];
      const zipCode = zipCodeInput && zipCodeInput.value;
      sitecoreFormData.postalZipCode = zipCode;

      const mobilePhoneInput = formDiv.querySelectorAll("input[type=tel]")[0];
      const mobilePhone = mobilePhoneInput && mobilePhoneInput.value;
      sitecoreFormData.phoneNumber = mobilePhone;

      const commentsTextInput = formDiv.querySelectorAll("textarea")[0];
      const commentsText = commentsTextInput && commentsTextInput.value;
      sitecoreFormData.mhComments = commentsText;

      //radios
      const allCheckBoxes = formDiv.querySelectorAll("input[type=checkbox]");
      const allCheckBoxesArray = Array.from(allCheckBoxes);
      const isAgentCheckBoxInput = allCheckBoxesArray.find((e) =>
        e.name.includes(dynamicsInputFieldsIds.dynaMXIsAgentCheckBoxId)
      );
      const isAgentCheckBox =
        isAgentCheckBoxInput && isAgentCheckBoxInput.checked;
      sitecoreFormData.isAgentCheck = isAgentCheckBox;

      const messageTextCheck = allCheckBoxesArray.find((e) =>
        e.name.includes(dynamicsInputFieldsIds.dynaMXIsTextMessageCheckBoxId)
      );
      const smsChecked = messageTextCheck && messageTextCheck.checked;
      sitecoreFormData.smsChecked = smsChecked;

      const consentCheckBoxInput = allCheckBoxesArray.find((e) =>
        e.name.includes(dynamicsInputFieldsIds.dynaMXIsConsentCheckBoxId)
      );
      const consentCheckBox =
        consentCheckBoxInput && consentCheckBoxInput.checked;
      sitecoreFormData.consentCheck = consentCheckBox;

      sitecoreFormData.promoCode =
        dynamicsFormFields && dynamicsFormFields.promoCode
          ? dynamicsFormFields.promoCode
          : "";

      sitecoreFormData.mpcCode = dynamicsFormFields && dynamicsFormFields.mpc;
      if (hasCommunityDropdown) {
        const communityOFInterestInput = formDiv.querySelectorAll("select")[0];
        const sCommunityOFInterest =
          communityOFInterestInput && communityOFInterestInput.value;
        const mpcCode = marketCommunitiesForm.find(
          (e) => e.communityID === sCommunityOFInterest
        );

        sitecoreFormData.mpcCode = mpcCode && mpcCode.mpcCode;
      }

      if (hasCommunityDropdown) {
        const communityOFInterestInput = formDiv.querySelectorAll("select")[0];
        const sCommunityOFInterest =
          communityOFInterestInput && communityOFInterestInput.value;
        const communityName = marketCommunitiesForm.find(
          (e) => e.communityID === sCommunityOFInterest
        );
        sitecoreFormData.JDECommunity =
          communityName && communityName.displayName;
      }

      sitecoreFormData.JDELot =
        dynamicsFormFields && dynamicsFormFields.qmiLotNumber
          ? dynamicsFormFields.qmiLotNumber
          : "";

      sitecoreFormData.divisionCode =
        dynamicsFormFields && dynamicsFormFields.divisionCode;
      if (hasCommunityDropdown) {
        const communityOFInterestInput = formDiv.querySelectorAll("select")[0];
        const sCommunityOFInterest =
          communityOFInterestInput && communityOFInterestInput.value;
        const divisionCode = marketCommunitiesForm.find(
          (e) => e.communityID === sCommunityOFInterest
        );
        sitecoreFormData.divisionCode =
          divisionCode && divisionCode.divisionCode;
      }

      sitecoreFormData.planName =
        dynamicsFormFields && dynamicsFormFields.planName
          ? dynamicsFormFields.planName
          : "";

      sitecoreFormData.qmiName =
        dynamicsFormFields && dynamicsFormFields.qmiName
          ? dynamicsFormFields.qmiName
          : "";

      sitecoreFormData.urlForm = window.location.href;

      sitecoreFormData.gaClientId = getGAClientId();
      sitecoreFormData.gaSessionId = getGASessionId();
      if (utmParams.utmSource) sitecoreFormData.utmSource = utmParams.utmSource;
      if (utmParams.utmMedium) sitecoreFormData.utmMedium = utmParams.utmMedium;
      if (utmParams.utmCampaign)
        sitecoreFormData.utmCampaign = utmParams.utmCampaign;
      if (utmParams.utmTerm) sitecoreFormData.utmTerm = utmParams.utmTerm;
      if (utmParams.utmContent)
        sitecoreFormData.utmContent = utmParams.utmContent;

      if (dynamicsInputFieldsIds.isGenericForm) {
        const allDropdowns = formDiv.getElementsByTagName("select");
        const allDropdownsArray = Array.from(allDropdowns);
        if (dynamicsInputFieldsIds.dynaMXDropdown1ID) {
          const dd1Input = allDropdownsArray.find((e) =>
            e.name.includes(dynamicsInputFieldsIds.dynaMXDropdown1ID)
          );
          sitecoreFormData.dd1 = dd1Input
            ? dd1Input.title +
              ": " +
              dd1Input.options[dd1Input.selectedIndex].text
            : "";
        } else {
          sitecoreFormData.dd1 = "";
        }
        if (dynamicsInputFieldsIds.dynaMXDropdown2ID) {
          const dd2Input = allDropdownsArray.find((e) =>
            e.name.includes(dynamicsInputFieldsIds.dynaMXDropdown2ID)
          );
          sitecoreFormData.dd2 = dd2Input
            ? dd2Input.title +
              ": " +
              dd2Input.options[dd2Input.selectedIndex].text
            : "";
        } else {
          sitecoreFormData.dd2 = "";
        }
        if (dynamicsInputFieldsIds.dynaMXDropdown3ID) {
          const dd3Input = allDropdownsArray.find((e) =>
            e.name.includes(dynamicsInputFieldsIds.dynaMXDropdown3ID)
          );
          sitecoreFormData.dd3 = dd3Input
            ? dd3Input.title +
              ": " +
              dd3Input.options[dd3Input.selectedIndex].text
            : "";
        } else {
          sitecoreFormData.dd3 = "";
        }
        if (dynamicsInputFieldsIds.dynaMXPriceRangesDDId) {
          const priceRanges = allDropdownsArray.find((e) =>
            e.name.includes(dynamicsInputFieldsIds.dynaMXPriceRangesDDId)
          );
          sitecoreFormData.priceRanges = priceRanges ? priceRanges.value : "";
        } else {
          sitecoreFormData.priceRanges = "";
        }
        if (dynamicsInputFieldsIds.dynaMXHomeTypesDDId) {
          const homeTypes = allDropdownsArray.find((e) =>
            e.name.includes(dynamicsInputFieldsIds.dynaMXHomeTypesDDId)
          );
          sitecoreFormData.homeTypes = homeTypes ? homeTypes.value : "";
        } else {
          sitecoreFormData.homeTypes = "";
        }
      }
    }
  }
  return sitecoreFormData;
};

const getMappings = ({
  formId,
  hasCommunityDropdown,
  dynamicsFormFields,
  marketCommunitiesForm,
  dynamicsInputFieldsIds,
  utmParams,
}) => {
  const sitecoreForm = document.getElementById(formId);
  const firstNameInput = sitecoreForm.querySelectorAll("input[type=text]")[3];
  const lastNameInput = sitecoreForm.querySelectorAll("input[type=text]")[4];
  const emailInput = sitecoreForm.querySelector("input[type=email]");
  const countryInput = hasCommunityDropdown
    ? sitecoreForm.querySelectorAll("select")[1]
    : sitecoreForm.querySelectorAll("select")[0];
  const zipCodeInput = sitecoreForm.querySelectorAll("input[type=text]")[5];
  const mobilePhoneSitecoreInput = sitecoreForm.querySelectorAll(
    "input[type=tel]"
  )[0];
  const commentsTextInput = sitecoreForm.querySelectorAll("textarea")[0];
  //radios
  const allCheckBoxes = sitecoreForm.querySelectorAll("input[type=checkbox]");
  const allCheckBoxesArray = Array.from(allCheckBoxes);
  const isAgentCheckBoxInput = allCheckBoxesArray.find((e) =>
    e.name.includes(dynamicsInputFieldsIds.dynaMXIsAgentCheckBoxId)
  );
  const isTextMessageCheckBoxInput = allCheckBoxesArray.find((e) =>
    e.name.includes(dynamicsInputFieldsIds.dynaMXIsTextMessageCheckBoxId)
  );
  const consentCheckBoxInput = allCheckBoxesArray.find((e) =>
    e.name.includes(dynamicsInputFieldsIds.dynaMXIsConsentCheckBoxId)
  );

  const communityOFInterestInput = hasCommunityDropdown
    ? marketCommunitiesForm.find(
        (e) =>
          e.communityID === sitecoreForm.querySelectorAll("select")[0].value
      ).mpcCode
    : dynamicsFormFields.mpc;
  const isMpcInput = sitecoreForm.querySelectorAll("input[name='mpcInput']")[0];
  if (isMpcInput) {
    isMpcInput.remove();
  }
  const mpcInput = document.createElement("input");
  mpcInput.setAttribute("type", "text");
  mpcInput.setAttribute("name", "mpcInput");
  mpcInput.setAttribute("class", "hidden");
  mpcInput.setAttribute("value", communityOFInterestInput || "");
  sitecoreForm.appendChild(mpcInput);

  const isDivisionAreaCodeInput = sitecoreForm.querySelectorAll(
    "input[name='divisionAreaCodeInput']"
  )[0];
  if (isDivisionAreaCodeInput) {
    isDivisionAreaCodeInput.remove();
  }
  const divisionAreaCodeInput = document.createElement("input");
  divisionAreaCodeInput.setAttribute("type", "text");
  divisionAreaCodeInput.setAttribute("name", "divisionAreaCodeInput");
  divisionAreaCodeInput.setAttribute("class", "hidden");
  divisionAreaCodeInput.setAttribute(
    "value",
    dynamicsFormFields.divisionCode || ""
  );
  sitecoreForm.appendChild(divisionAreaCodeInput);

  const isSiteUrlInput = sitecoreForm.querySelectorAll(
    "input[name='siteUrlInput']"
  )[0];
  if (isSiteUrlInput) {
    isSiteUrlInput.remove();
  }
  const siteUrlInput = document.createElement("input");
  siteUrlInput.setAttribute("type", "text");
  siteUrlInput.setAttribute("name", "siteUrlInput");
  siteUrlInput.setAttribute("class", "hidden");
  siteUrlInput.setAttribute("value", window.location.href || "");
  sitecoreForm.appendChild(siteUrlInput);

  const isPromoCodeInput = sitecoreForm.querySelectorAll(
    "input[name='promoCodeInput']"
  )[0];
  if (isPromoCodeInput) {
    isPromoCodeInput.remove();
  }
  const promoCodeInput = document.createElement("input");
  promoCodeInput.setAttribute("type", "text");
  promoCodeInput.setAttribute("name", "promoCodeInput");
  promoCodeInput.setAttribute("class", "hidden");
  promoCodeInput.setAttribute("value", dynamicsFormFields.promoCode || "");
  sitecoreForm.appendChild(promoCodeInput);

  const isJdeLotNumberInput = sitecoreForm.querySelectorAll(
    "input[name='jdeLotNumberInput']"
  )[0];
  if (isJdeLotNumberInput) {
    isJdeLotNumberInput.remove();
  }
  const jdeLotNumberInput = document.createElement("input");
  jdeLotNumberInput.setAttribute("type", "text");
  jdeLotNumberInput.setAttribute("name", "jdeLotNumberInput");
  jdeLotNumberInput.setAttribute("class", "hidden");
  jdeLotNumberInput.setAttribute(
    "value",
    dynamicsFormFields.qmiLotNumber || ""
  );
  sitecoreForm.appendChild(jdeLotNumberInput);

  const isPlanNameInput = sitecoreForm.querySelectorAll(
    "input[name='planNameInput']"
  )[0];
  if (isPlanNameInput) {
    isPlanNameInput.remove();
  }
  const planNameInput = document.createElement("input");
  planNameInput.setAttribute("type", "text");
  planNameInput.setAttribute("name", "planNameInput");
  planNameInput.setAttribute("class", "hidden");
  planNameInput.setAttribute("value", dynamicsFormFields.planName || "");
  sitecoreForm.appendChild(planNameInput);

  const isQmiAddressInput = sitecoreForm.querySelectorAll(
    "input[name='qmiAddressInput']"
  )[0];
  if (isQmiAddressInput) {
    isQmiAddressInput.remove();
  }
  const qmiAddressInput = document.createElement("input");
  qmiAddressInput.setAttribute("type", "text");
  qmiAddressInput.setAttribute("name", "qmiAddressInput");
  qmiAddressInput.setAttribute("class", "hidden");
  qmiAddressInput.setAttribute("value", dynamicsFormFields.qmiName || "");
  sitecoreForm.appendChild(qmiAddressInput);

  const isLeadsourcetypeInput = sitecoreForm.querySelectorAll(
    "input[name='leadsourcetypeInput']"
  )[0];
  if (isLeadsourcetypeInput) {
    isLeadsourcetypeInput.remove();
  }
  const leadsourcetypeInput = document.createElement("input");
  leadsourcetypeInput.setAttribute("type", "text");
  leadsourcetypeInput.setAttribute("name", "leadsourcetypeInput");
  leadsourcetypeInput.setAttribute("class", "hidden");
  leadsourcetypeInput.setAttribute("value", "860890003");
  sitecoreForm.appendChild(leadsourcetypeInput);

  const isMobilePhoneInput = sitecoreForm.querySelectorAll(
    "input[name='mobilePhoneInput']"
  )[0];
  if (isMobilePhoneInput) {
    isMobilePhoneInput.remove();
  }
  const mobilePhoneInput = document.createElement("input");
  mobilePhoneInput.setAttribute("type", "text");
  mobilePhoneInput.setAttribute("name", "mobilePhoneInput");
  mobilePhoneInput.setAttribute("class", "hidden");
  const email = mobilePhoneSitecoreInput.value.replace(/\D/g, "");
  mobilePhoneInput.setAttribute("value", email);
  sitecoreForm.appendChild(mobilePhoneInput);

  const isRealEstateAgentInputExists = sitecoreForm.querySelectorAll(
    "input[name='isRealEstateAgentInput']"
  )[0];
  if (isRealEstateAgentInputExists) {
    isRealEstateAgentInputExists.remove();
  }
  const isRealEstateAgentInput = document.createElement("input");
  isRealEstateAgentInput.setAttribute("type", "text");
  isRealEstateAgentInput.setAttribute("name", "isRealEstateAgentInput");
  isRealEstateAgentInput.setAttribute("class", "hidden");
  const isAgent = isAgentCheckBoxInput.checked;
  isRealEstateAgentInput.setAttribute("value", isAgent);
  sitecoreForm.appendChild(isRealEstateAgentInput);

  const isExpressedConsentInput = sitecoreForm.querySelectorAll(
    "input[name='expressedConsentInput']"
  )[0];
  if (isExpressedConsentInput) {
    isExpressedConsentInput.remove();
  }
  const expressedConsentInput = document.createElement("input");
  expressedConsentInput.setAttribute("type", "text");
  expressedConsentInput.setAttribute("name", "expressedConsentInput");
  expressedConsentInput.setAttribute("class", "hidden");
  const isConsent = consentCheckBoxInput.checked;
  expressedConsentInput.setAttribute("value", isConsent);
  sitecoreForm.appendChild(expressedConsentInput);

  const isTextMessageInput = sitecoreForm.querySelectorAll(
    "input[name='textMessageInput']"
  )[0];
  if (isTextMessageInput) {
    isTextMessageInput.remove();
  }
  const textMessageInput = document.createElement("input");
  textMessageInput.setAttribute("type", "text");
  textMessageInput.setAttribute("name", "textMessageInput");
  textMessageInput.setAttribute("class", "hidden");
  const isTextMessage = isTextMessageCheckBoxInput.checked;
  textMessageInput.setAttribute("value", isTextMessage);
  sitecoreForm.appendChild(textMessageInput);

  const isGAClientIdInput = sitecoreForm.querySelectorAll(
    "input[name='gaClientIdInput']"
  )[0];
  if (isGAClientIdInput) {
    isGAClientIdInput.remove();
  }
  const gaClientIdInput = document.createElement("input");
  gaClientIdInput.setAttribute("type", "text");
  gaClientIdInput.setAttribute("name", "gaClientIdInput");
  gaClientIdInput.setAttribute("class", "hidden");
  gaClientIdInput.setAttribute("value", getGAClientId());
  sitecoreForm.appendChild(gaClientIdInput);

  const isGASessionIdInput = sitecoreForm.querySelectorAll(
    "input[name='gaSessionIdInput']"
  )[0];
  if (isGASessionIdInput) {
    isGASessionIdInput.remove();
  }
  const gaSessionIdInput = document.createElement("input");
  gaSessionIdInput.setAttribute("type", "text");
  gaSessionIdInput.setAttribute("name", "gaSessionIdInput");
  gaSessionIdInput.setAttribute("class", "hidden");
  gaSessionIdInput.setAttribute("value", getGASessionId());
  sitecoreForm.appendChild(gaSessionIdInput);

  const allDropdowns = sitecoreForm.getElementsByTagName("select");
  const allDropdownsArray = Array.from(allDropdowns);
  const isPrice1Dropdown = sitecoreForm.querySelectorAll(
    "input[name='dd1']"
  )[0];
  if (isPrice1Dropdown) {
    isPrice1Dropdown.remove();
  }
  const price1DropdownHidden = document.createElement("input");
  price1DropdownHidden.setAttribute("type", "text");
  price1DropdownHidden.setAttribute("name", "dd1");
  price1DropdownHidden.setAttribute("class", "hidden");
  if (dynamicsInputFieldsIds.dynaMXDropdown1ID) {
    const dd1Value = allDropdownsArray.find((e) =>
      e.name.includes(dynamicsInputFieldsIds.dynaMXDropdown1ID)
    );
    if (dd1Value) {
      price1DropdownHidden.setAttribute(
        "value",
        dd1Value.title + ": " + dd1Value.options[dd1Value.selectedIndex].text ||
          ""
      );
      sitecoreForm.appendChild(price1DropdownHidden);
    }
  } else {
    price1DropdownHidden.setAttribute("value", "");
    sitecoreForm.appendChild(price1DropdownHidden);
  }

  const isPrice2Dropdown = sitecoreForm.querySelectorAll(
    "input[name='dd2']"
  )[0];
  if (isPrice2Dropdown) {
    isPrice2Dropdown.remove();
  }
  const price2DropdownHidden = document.createElement("input");
  price2DropdownHidden.setAttribute("type", "text");
  price2DropdownHidden.setAttribute("name", "dd2");
  price2DropdownHidden.setAttribute("class", "hidden");
  if (dynamicsInputFieldsIds.dynaMXDropdown2ID) {
    const dd2Value = allDropdownsArray.find((e) =>
      e.name.includes(dynamicsInputFieldsIds.dynaMXDropdown2ID)
    );
    if (dd2Value) {
      price2DropdownHidden.setAttribute(
        "value",
        dd2Value.title + ": " + dd2Value.options[dd2Value.selectedIndex].text ||
          ""
      );
      sitecoreForm.appendChild(price2DropdownHidden);
    }
  } else {
    price2DropdownHidden.setAttribute("value", "");
    sitecoreForm.appendChild(price2DropdownHidden);
  }

  const isPrice3Dropdown = sitecoreForm.querySelectorAll(
    "input[name='dd3']"
  )[0];
  if (isPrice3Dropdown) {
    isPrice3Dropdown.remove();
  }
  const price3DropdownHidden = document.createElement("input");
  price3DropdownHidden.setAttribute("type", "text");
  price3DropdownHidden.setAttribute("name", "dd3");
  price3DropdownHidden.setAttribute("class", "hidden");
  if (dynamicsInputFieldsIds.dynaMXDropdown3ID) {
    const dd3Value = allDropdownsArray.find((e) =>
      e.name.includes(dynamicsInputFieldsIds.dynaMXDropdown3ID)
    );
    if (dd3Value) {
      price3DropdownHidden.setAttribute(
        "value",
        dd3Value.title + ": " + dd3Value.options[dd3Value.selectedIndex].text ||
          ""
      );
      sitecoreForm.appendChild(price3DropdownHidden);
    }
  } else {
    price3DropdownHidden.setAttribute("value", "");
    sitecoreForm.appendChild(price3DropdownHidden);
  }

  const isPriceRangesDropdown = sitecoreForm.querySelectorAll(
    "input[name='priceRanges']"
  )[0];
  if (isPriceRangesDropdown) {
    isPriceRangesDropdown.remove();
  }
  const priceRangesDropdownHidden = document.createElement("input");
  priceRangesDropdownHidden.setAttribute("type", "text");
  priceRangesDropdownHidden.setAttribute("name", "priceRanges");
  priceRangesDropdownHidden.setAttribute("class", "hidden");
  if (dynamicsInputFieldsIds.dynaMXPriceRangesDDId) {
    const priceRangesValue = allDropdownsArray.find((e) =>
      e.name.includes(dynamicsInputFieldsIds.dynaMXPriceRangesDDId)
    );
    if (priceRangesValue) {
      priceRangesDropdownHidden.setAttribute("value", priceRangesValue.value);
      sitecoreForm.appendChild(priceRangesDropdownHidden);
    }
  } else {
    priceRangesDropdownHidden.setAttribute("value", "");
    sitecoreForm.appendChild(priceRangesDropdownHidden);
  }

  const isHomeTypesDropdown = sitecoreForm.querySelectorAll(
    "input[name='homeTypes']"
  )[0];
  if (isHomeTypesDropdown) {
    isHomeTypesDropdown.remove();
  }
  const homeTypesDropdownHidden = document.createElement("input");
  homeTypesDropdownHidden.setAttribute("type", "text");
  homeTypesDropdownHidden.setAttribute("name", "homeTypes");
  homeTypesDropdownHidden.setAttribute("class", "hidden");
  if (dynamicsInputFieldsIds.dynaMXHomeTypesDDId) {
    const homeTypesValue = allDropdownsArray.find((e) =>
      e.name.includes(dynamicsInputFieldsIds.dynaMXHomeTypesDDId)
    );
    if (homeTypesValue) {
      homeTypesDropdownHidden.setAttribute("value", homeTypesValue.value);
      sitecoreForm.appendChild(homeTypesDropdownHidden);
    }
  } else {
    homeTypesDropdownHidden.setAttribute("value", "");
    sitecoreForm.appendChild(homeTypesDropdownHidden);
  }

  const isUTMSource = sitecoreForm.querySelectorAll(
    "input[name='utmSourceInput']"
  )[0];
  if (isUTMSource) {
    isUTMSource.remove();
  }
  const utmSourceInput = document.createElement("input");
  utmSourceInput.setAttribute("type", "text");
  utmSourceInput.setAttribute("name", "utmSourceInput");
  utmSourceInput.setAttribute("class", "hidden");
  if (utmParams.utmSource) {
    utmSourceInput.setAttribute("value", utmParams.utmSource);
    sitecoreForm.appendChild(utmSourceInput);
  }
  const isUTMMedium = sitecoreForm.querySelectorAll(
    "input[name='utmMediumInput']"
  )[0];
  if (isUTMMedium) {
    isUTMMedium.remove();
  }
  const utmMediumInput = document.createElement("input");
  utmMediumInput.setAttribute("type", "text");
  utmMediumInput.setAttribute("name", "utmMediumInput");
  utmMediumInput.setAttribute("class", "hidden");
  if (utmParams.utmMedium) {
    utmMediumInput.setAttribute("value", utmParams.utmMedium);
    sitecoreForm.appendChild(utmMediumInput);
  }
  const isUTMCampaign = sitecoreForm.querySelectorAll(
    "input[name='utmCampaignInput']"
  )[0];
  if (isUTMCampaign) {
    isUTMCampaign.remove();
  }
  const utmCampaignInput = document.createElement("input");
  utmCampaignInput.setAttribute("type", "text");
  utmCampaignInput.setAttribute("name", "utmCampaignInput");
  utmCampaignInput.setAttribute("class", "hidden");
  if (utmParams.utmCampaign) {
    utmCampaignInput.setAttribute("value", utmParams.utmCampaign);
    sitecoreForm.appendChild(utmCampaignInput);
  }

  const isUTMTerm = sitecoreForm.querySelectorAll(
    "input[name='utmTermInput']"
  )[0];
  if (isUTMTerm) {
    isUTMTerm.remove();
  }
  const utmTermInput = document.createElement("input");
  utmTermInput.setAttribute("type", "text");
  utmTermInput.setAttribute("name", "utmTermInput");
  utmTermInput.setAttribute("class", "hidden");
  if (utmParams.utmTerm) {
    utmTermInput.setAttribute("value", utmParams.utmTerm);
    sitecoreForm.appendChild(utmTermInput);
  }

  const isUTMContent = sitecoreForm.querySelectorAll(
    "input[name='utmContentInput']"
  )[0];
  if (isUTMContent) {
    isUTMContent.remove();
  }
  const utmContentInput = document.createElement("input");
  utmContentInput.setAttribute("type", "text");
  utmContentInput.setAttribute("name", "utmContentInput");
  utmContentInput.setAttribute("class", "hidden");
  if (utmParams.utmContent) {
    utmContentInput.setAttribute("value", utmParams.utmContent);
    sitecoreForm.appendChild(utmContentInput);
  }

  const isCreationMethodInput = sitecoreForm.querySelectorAll(
    "input[name='creationMethodInput']"
  )[0];
  if (isCreationMethodInput) {
    isCreationMethodInput.remove();
  }
  const creationMethodInput = document.createElement("input");
  creationMethodInput.setAttribute("type", "text");
  creationMethodInput.setAttribute("name", "creationMethodInput");
  creationMethodInput.setAttribute("class", "hidden");
  creationMethodInput.setAttribute("value", "860890005");
  sitecoreForm.appendChild(creationMethodInput);

  const sitecoreFormFields = {
    firstName: firstNameInput.name,
    lastName: lastNameInput.name,
    email: emailInput.name,
    countryOfResidence: countryInput.name,
    zipcode: zipCodeInput.name,
    phone: mobilePhoneInput.name,
    isRealEstateAgent: isRealEstateAgentInput.name,
    comments: commentsTextInput.name,
    mpcCode: mpcInput.name,
    divisionAreaCode: divisionAreaCodeInput.name,
    siteUrl: siteUrlInput.name,
    promocode: promoCodeInput.name,
    qmiaddress: qmiAddressInput.name,
    jdeLotNumber: jdeLotNumberInput.name,
    planName: planNameInput.name,
    leadsourcetype: leadsourcetypeInput.name,
    consent: expressedConsentInput.name,
    textMessage: textMessageInput.name,
    jdeCommunityNumber: "",
    elevationName: "",
    creationMethod: creationMethodInput.name,
    gaClientId: gaClientIdInput.name,
    gaSessionId: gaSessionIdInput.name,
  };

  if (utmParams.utmSource) sitecoreFormFields.utmSource = utmSourceInput.name;
  if (utmParams.utmMedium) sitecoreFormFields.utmMedium = utmMediumInput.name;
  if (utmParams.utmCampaign)
    sitecoreFormFields.utmCampaign = utmCampaignInput.name;
  if (utmParams.utmTerm) sitecoreFormFields.utmTerm = utmTermInput.name;
  if (utmParams.utmContent)
    sitecoreFormFields.utmContent = utmContentInput.name;

  if (dynamicsInputFieldsIds.isGenericForm) {
    sitecoreFormFields.dd1 = price1DropdownHidden.name;
    sitecoreFormFields.dd2 = price2DropdownHidden.name;
    sitecoreFormFields.dd3 = price3DropdownHidden.name;
    sitecoreFormFields.priceRanges = priceRangesDropdownHidden.name;
    sitecoreFormFields.homeTypes = homeTypesDropdownHidden.name;
  }
  const mappings = [
    {
      FormFieldName: sitecoreFormFields.firstName,
      DataverseFieldName: "firstname",
    },
    {
      FormFieldName: sitecoreFormFields.lastName,
      DataverseFieldName: "lastname",
    },
    {
      FormFieldName: sitecoreFormFields.email,
      DataverseFieldName: "emailaddress1",
    },
    {
      FormFieldName: sitecoreFormFields.phone,
      DataverseFieldName: "mobilephone",
    },

    {
      FormFieldName: sitecoreFormFields.zipcode,
      DataverseFieldName: "address1_postalcode",
    },
    {
      FormFieldName: sitecoreFormFields.isRealEstateAgent,
      DataverseFieldName: "mam_web_isrealestateagent",
      DataverseFieldValue: [
        { FormValue: "true", DataverseValue: "1" }, // Yes
        { FormValue: "false", DataverseValue: "0" }, // No
      ],
    },

    {
      FormFieldName: sitecoreFormFields.leadsourcetype,
      DataverseFieldName: "msdyncrm_leadsourcetype",
    },
    {
      FormFieldName: sitecoreFormFields.siteUrl,
      DataverseFieldName: "mam_mattamyhomesurl",
    },
    {
      FormFieldName: sitecoreFormFields.divisionAreaCode,
      DataverseFieldName: "mam_divisioncode",
    },
    {
      FormFieldName: sitecoreFormFields.mpcCode,
      DataverseFieldName: "mam_mpccode",
    },
    {
      FormFieldName: sitecoreFormFields.countryOfResidence,
      DataverseFieldName: "mam_marketingcountrycode",
      DataverseFieldValue: [
        { FormValue: "CA", DataverseValue: "860890000" }, // Canada
        { FormValue: "US", DataverseValue: "860890001" }, // United States
        { FormValue: "Other", DataverseValue: "860890002" }, // Other
      ],
    },

    {
      FormFieldName: sitecoreFormFields.countryOfResidence,
      DataverseFieldName: "address1_country",
      DataverseFieldValue: [
        { FormValue: "CA", DataverseValue: "Canada" }, // Canada
        { FormValue: "US", DataverseValue: "United States" }, // United States
      ],
    },

    {
      FormFieldName: sitecoreFormFields.promocode,
      DataverseFieldName: "mam_promocode",
    },
    {
      FormFieldName: sitecoreFormFields.creationMethod,
      DataverseFieldName: "mam_creationmethodcode",
    },
    {
      FormFieldName: sitecoreFormFields.qmiaddress,
      DataverseFieldName: "mam_qmiaddress",
    },
    {
      FormFieldName: sitecoreFormFields.jdeCommunityNumber,
      DataverseFieldName: "mam_jdecommunitynumber",
    },
    {
      FormFieldName: sitecoreFormFields.jdeLotNumber,
      DataverseFieldName: "mam_jdelotnumber",
    },
    {
      FormFieldName: sitecoreFormFields.planName,
      DataverseFieldName: "mam_planname",
    },
    {
      FormFieldName: sitecoreFormFields.elevationName,
      DataverseFieldName: "mam_elevationname",
    },
    {
      FormFieldName: sitecoreFormFields.comments,
      DataverseFieldName: "mam_comments",
    },
    {
      FormFieldName: sitecoreFormFields.consent,
      DataverseFieldName: "mam_web_ismarketingexpressedconsent",
      DataverseFieldValue: [
        { FormValue: "true", DataverseValue: "1" }, // Yes
        { FormValue: "false", DataverseValue: "0" }, // No
      ],
    },
    {
      FormFieldName: sitecoreFormFields.gaClientId,
      DataverseFieldName: "mam_clientid",
    },
    {
      FormFieldName: sitecoreFormFields.gaSessionId,
      DataverseFieldName: "mam_sessionid",
    },
    {
      FormFieldName: sitecoreFormFields.textMessage,
      DataverseFieldName: "trd_donotsms",
      DataverseFieldValue: [
        { FormValue: "true", DataverseValue: "0" },
        { FormValue: "false", DataverseValue: "1" },
      ],
    },
  ];

  if (utmParams.utmSource) {
    mappings.push({
      FormFieldName: sitecoreFormFields.utmSource,
      DataverseFieldName: "mam_utmsource",
    });
  }
  if (utmParams.utmMedium) {
    mappings.push({
      FormFieldName: sitecoreFormFields.utmMedium,
      DataverseFieldName: "mam_utmmedium",
    });
  }
  if (utmParams.utmCampaign) {
    mappings.push({
      FormFieldName: sitecoreFormFields.utmCampaign,
      DataverseFieldName: "mam_utmcampaign",
    });
  }
  if (utmParams.utmTerm) {
    mappings.push({
      FormFieldName: sitecoreFormFields.utmTerm,
      DataverseFieldName: "mam_utmterm",
    });
  }
  if (utmParams.utmContent) {
    mappings.push({
      FormFieldName: sitecoreFormFields.utmContent,
      DataverseFieldName: "mam_utmcontent",
    });
  }

  if (dynamicsInputFieldsIds.isGenericForm) {
    mappings.push({
      FormFieldName: sitecoreFormFields.dd1,
      DataverseFieldName: "mam_marketingcustomfield1",
    });
    mappings.push({
      FormFieldName: sitecoreFormFields.dd2,
      DataverseFieldName: "mam_marketingcustomfield2",
    });
    mappings.push({
      FormFieldName: sitecoreFormFields.dd3,
      DataverseFieldName: "mam_marketingcustomfield3",
    });
    mappings.push({
      FormFieldName: sitecoreFormFields.homeTypes,
      DataverseFieldName: "mam_hometype",
    });
    mappings.push({
      FormFieldName: sitecoreFormFields.priceRanges,
      DataverseFieldName: "mam_pricerange",
    });
  }

  return mappings;
};

const Form = ({
  fields,
  enableDynaMXFormAPI = false,
  dynamicsFormFields = {},
  isGenericForm = false,
}) => {
  //console.log({ dynamicsFormFields })
  const [_, dispatch] = useUiValue();
  const isInvestorRelationsForm =
    fields &&
    fields.metadata &&
    (fields.metadata.name === "InvestorRelations" ||
      fields.metadata.name === "Warranty");
  const isScheduleAVisitForm =
    fields && fields.metadata && !isInvestorRelationsForm;
  const [isShowGreenScreen, setIsShowGreenScreen] = useState(false);
  /*const [fetchNumber, setFetchNumber] = useState(0);
  const [fetchError, setFetchError] = useState(false);
  const [fetchFormData, setFormData] = useState(false);
  const [fetchFormEndpoint, setFormEndpoint] = useState(false);*/

  const formTitle = fields && fields.metadata && fields.metadata.name;

  const { ContextID = {} } = useSitecoreContext() || {};
  const [{ country }] = useCountryValue();
  const history = useHistory();

  const {
    marketCommunitiesForm,
    showMarketCommunitiesForm,
  } = useMarketCommunitiesForm();
  const {
    dynaMXFormID,
    dynaMXFormAPIURL,
    dynaMXCountryInputID,
    dynaMXDropdown1ID,
    dynaMXDropdown2ID,
    dynaMXDropdown3ID,
    dynaMXPriceRangesDDId,
    dynaMXHomeTypesDDId,
    dynaMXIsAgentCheckBoxId,
    dynaMXIsTextMessageCheckBoxId,
    dynaMXIsConsentCheckBoxId,
  } = useDynamicsForm();

  const dynamicsInputFieldsIds = {
    dynaMXCountryInputID,
    dynaMXDropdown1ID,
    dynaMXDropdown2ID,
    dynaMXDropdown3ID,
    dynaMXPriceRangesDDId,
    dynaMXHomeTypesDDId,
    dynaMXIsAgentCheckBoxId,
    dynaMXIsTextMessageCheckBoxId,
    dynaMXIsConsentCheckBoxId,
    isGenericForm,
  };
  let logList = [];
  const { saveLogs } = useConsoleLogging();
  const addNewLog = (level, msg) => {
    logList.push({ level, msg });
  };

  const formRef = useRef();
  /*useEffect(() => {
    if (fetchError && fetchNumber < 2) {
      formFetcher(fetchFormData, fetchFormEndpoint);
      setFetchError(false);
    }
  }, [fetchError]);*/
  const location = useLocation();
  const params = queryString.parse(location.search);

  const formFetcher = (formData, endpoint) => {
    let sitecoreFormData = {};
    let mappings = [];
    const utmParams = {
      utmSource: params.utm_source,
      utmMedium: params.utm_medium,
      utmCampaign: params.utm_campaign,
      utmTerm: params.utm_term,
      utmContent: params.utm_content,
    };
    //console.log({ utmParams });
    if (typeof window !== "undefined") {
      const formDiv = document.getElementById(formTitle || "Form");

      if (formDiv) {
        const fNameInput = formDiv.querySelectorAll("input[type=text]")[3];
        const firstName = fNameInput && fNameInput.value;
        if (firstName) {
          fNameInput.value = getNameFormTextFormat(firstName);
        }
        const lNameInput = formDiv.querySelectorAll("input[type=text]")[4];
        const lastName = lNameInput && lNameInput.value;
        if (lastName) {
          lNameInput.value = getNameFormTextFormat(lastName);
        }

        const emailInput = formDiv.querySelector("input[type=email]");
        const email = emailInput && emailInput.value;
        if (email && !validateEmail(email)) {
          console.log("Something went wrong on email: " + email + ".");
          return {
            success: false,
            errors: "Something went wrong. Error was thrown when submit form",
          };
        }
        const textInputs = formDiv.querySelectorAll("input[type=text]");
        let zipInput;
        for (let i = 0; i < textInputs.length; i++) {
          if (
            textInputs[i].ariaLabel &&
            (textInputs[i].ariaLabel.includes("Zip") ||
              textInputs[i].ariaLabel.includes("zip"))
          ) {
            zipInput = textInputs[i];
            break;
          }
        }
        const zip = zipInput && zipInput.value;
        if (zip && country && !validateZipCode(zip, country)) {
          console.log("Something went wrong with the ZIP: " + zip + ".");
          return {
            success: false,
            errors: "Something went wrong. Error was thrown when submit form",
          };
        }
      }
    }

    if (enableDynaMXFormAPI) {
      addNewLog(
        100,
        "==============[Dynamics Forms API] " + formTitle + "=============="
      );
      if (window && window.d365mktformcapture) {
        //console.log("variable d365mktformcapture creada");
        addNewLog(100, "[Dynamics Forms API] The Dynamics Form API is enabled");
        //console.log("form submitted", mappings);
      } else {
        addNewLog(
          300,
          "[Dynamics Forms API] Something went wrong when trying to load the The Dynamics Form API script"
        );
        addNewLog(
          100,
          "[Dynamics Forms API] Trying to reload the scripts on form submit"
        );
        let formCaptureScript = document.createElement("script");
        formCaptureScript.src =
          "https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/can/FormCapture/FormCapture.bundle.js";
        formCaptureScript.onload = () => {
          addNewLog(
            100,
            "[Dynamics Forms API] Dynamics Forms API script loaded"
          );
        };
        formCaptureScript.onerror = (e) => {
          addNewLog(
            300,
            "[Dynamics Forms API] Something went wrong when trying to load the The Dynamics Form API script"
          );
        };
        document.head.appendChild(formCaptureScript);
      }
      mappings = getMappings({
        formId: "Sitecore" + (formTitle || "Form"),
        hasCommunityDropdown: showMarketCommunitiesForm,
        dynamicsFormFields,
        marketCommunitiesForm,
        dynamicsInputFieldsIds,
        utmParams,
      });
      sitecoreFormData = getSitecoreFormData(
        formTitle,
        dynamicsFormFields,
        showMarketCommunitiesForm,
        marketCommunitiesForm,
        dynamicsInputFieldsIds,
        utmParams
      );
      const getInTouchForm = document
        .getElementById(formTitle || "Form")
        .getElementsByTagName("form")[0];
      const submitButon = getInTouchForm.querySelectorAll("button")[0];
      const getInTouchFormData = new FormData(getInTouchForm, submitButon);
      if (submitButon && getInTouchForm) {
        let getInTouchFormDataString = "";

        for (const [key, value] of getInTouchFormData) {
          const field = document.getElementsByName(key)[0];
          const fieldLabel =
            field &&
            field.labels &&
            field.labels[0] &&
            field.labels[0].textContent;
          if (fieldLabel && value)
            getInTouchFormDataString += `${fieldLabel}: ${value}  |  `;
        }
        addNewLog(
          100,
          "[Dynamics Forms API] Form data sended to Sitecore. ContextID: " +
            ContextID.value +
            "  |  " +
            getInTouchFormDataString
        );
      }
    }

    setIsShowGreenScreen(true);
    /*setFetchNumber(fetchNumber + 1);
    setFormData(formData);
    setFormEndpoint(endpoint);*/
    return fetch(endpoint + "&itemId=" + ContextID.value, {
      body: formData.toUrlEncodedFormData(),
      method: "post",
      // IMPORTANT: Sitecore forms relies on cookies for some state management, so credentials must be included.
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    })
      .then((res) => {
        if (res.ok) {
          //setFetchError(false);
          if (isScheduleAVisitForm) {
            track({ eventId: trackingEvents.RegisterForUpdates });
          }
          if (enableDynaMXFormAPI) {
            addNewLog(
              100,
              "[Dynamics Forms API] Sitecore form fetch response is 200"
            );
            if (window && window.d365mktformcapture) {
              //console.log("form submitted ok");
              const sitecoreForm = document.getElementById(
                "Sitecore" + (formTitle || "Form")
              );
              const serializedForm = window.d365mktformcapture.serializeForm(
                sitecoreForm,
                mappings
              );
              const payload = serializedForm.SerializedForm.build();

              const captureConfig = {
                FormId: dynaMXFormID,
                FormApiUrl: dynaMXFormAPIURL,
              };
              addNewLog(100, "[Dynamics Forms API] API payload" + payload.data);
              /*console.log({
                serializedForm,
                captureConfig,
                payload,
              });*/
              addNewLog(100, "[Dynamics Forms API] API called");
              window.d365mktformcapture
                .submitForm(captureConfig, payload)
                .then((res) => {
                  addNewLog(
                    100,
                    "[Dynamics Forms API] API called successfully"
                  );
                  saveLogs(logList, sitecoreFormData);
                })
                .catch((e) => {
                  console.log("d365mktformcapture.submitForm error", e);
                  addNewLog(
                    300,
                    "[Dynamics Forms API] Something went wrong. " + e
                  );
                  saveLogs(logList, sitecoreFormData);
                });
            }
          }
          try {
            switch (formTitle) {
              case "ScheduleAVisitMarket":
                if (
                  typeof window !== "undefined" &&
                  typeof window.gtag === "function"
                ) {
                  window.gtag("event", "MHFormSubmitMarketPage");
                }
                break;
              case "ScheduleAVisitPromo":
                if (
                  typeof window !== "undefined" &&
                  typeof window.gtag === "function"
                ) {
                  window.gtag("event", "MHFormSubmitPromoPage");
                }
                break;
              default:
                if (
                  typeof window !== "undefined" &&
                  typeof window.gtag === "function"
                ) {
                  window.gtag("event", "MHFormSubmitSuccess");
                }
                break;
            }
          } catch (e) {
            console.log("Something went wrong with gtag event log. " + e + ".");
          }
        } else {
          //setFetchError(true);
          if (enableDynaMXFormAPI) {
            addNewLog(
              300,
              "[Dynamics Forms API] Sitecore form fetch fail. " +
                "Status: " +
                res.status +
                ". Status Text: " +
                res.statusText
            );
            /*if (fetchNumber < 1) {
              addNewLog(
                300,
                "[Dynamics Forms API] Trying to submit the form again."
              );
            }*/
            saveLogs(logList, sitecoreFormData);
          }
        }
        setTimeout(function () {
          setIsShowGreenScreen(false);
          if (isInvestorRelationsForm) {
            if (formTitle === "InvestorRelations")
              dispatch(openSuccessModalInvestors());
            else dispatch(openSuccessModalWarranty());
          } else if (isScheduleAVisitForm) {
            dispatch(openSuccessModal());
            if (typeof window !== "undefined") {
              if (document.querySelectorAll("form>div>div>select").length > 0) {
                if (document.querySelectorAll("form>div>div>select")[0])
                  document.querySelectorAll(
                    "form>div>div>select"
                  )[0].selectedIndex = 0;
                if (document.querySelectorAll("form>div>div>select")[1])
                  document.querySelectorAll(
                    "form>div>div>select"
                  )[1].selectedIndex = 0;
                if (document.querySelectorAll("form>div>div>select")[2])
                  document.querySelectorAll(
                    "form>div>div>select"
                  )[2].selectedIndex = 0;
                if (document.querySelectorAll("form>div>div>select")[3])
                  document.querySelectorAll(
                    "form>div>div>select"
                  )[3].selectedIndex = 0;
                if (document.querySelectorAll("form>div>div>select")[4])
                  document.querySelectorAll(
                    "form>div>div>select"
                  )[4].selectedIndex = 0;
              } else {
                document.querySelector("form>div>div>select").selectedIndex = 0;
              }
            }
          } else {
            dispatch(openSuccessModalPromo());
          }
        }, 3000);
        if (typeof window !== "undefined") {
          document.querySelector("button[type=submit]").disabled = false;
        }
        return res.json();
      })
      .catch((e) => {
        console.log("Something went wrong when submit form. " + e + ".");
        setTimeout(function () {
          setIsShowGreenScreen(false);
          if (isInvestorRelationsForm) {
            if (formTitle === "InvestorRelations")
              dispatch(openSuccessModalInvestors());
            else dispatch(openSuccessModalWarranty());
          } else if (isScheduleAVisitForm) {
            dispatch(openSuccessModal());
            if (typeof window !== "undefined") {
              if (document.querySelectorAll("form>div>div>select").length > 0) {
                if (document.querySelectorAll("form>div>div>select")[0])
                  document.querySelectorAll(
                    "form>div>div>select"
                  )[0].selectedIndex = 0;
                if (document.querySelectorAll("form>div>div>select")[1])
                  document.querySelectorAll(
                    "form>div>div>select"
                  )[1].selectedIndex = 0;
                if (document.querySelectorAll("form>div>div>select")[2])
                  document.querySelectorAll(
                    "form>div>div>select"
                  )[2].selectedIndex = 0;
                if (document.querySelectorAll("form>div>div>select")[3])
                  document.querySelectorAll(
                    "form>div>div>select"
                  )[3].selectedIndex = 0;
                if (document.querySelectorAll("form>div>div>select")[4])
                  document.querySelectorAll(
                    "form>div>div>select"
                  )[4].selectedIndex = 0;
              } else {
                document.querySelector("form>div>div>select").selectedIndex = 0;
              }
            }
          } else {
            dispatch(openSuccessModalPromo());
          }
        }, 3000);
        if (typeof window !== "undefined") {
          document.querySelector("button[type=submit]").disabled = false;
        }
        return {
          success: false,
          errors: "Something went wrong. Error was thrown when submit form",
        };
      });
  };

  const FieldComponent = isInvestorRelationsForm
    ? InvestorRelationsField
    : isScheduleAVisitForm
    ? ScheduleAVisitField
    : OutlinedField;

  const LabelComponent =
    isInvestorRelationsForm || isScheduleAVisitForm
      ? LabelComponentInvestor
      : LabelComponentPromos;

  useEffect(() => {
    setTimeout(() => {
      const element = document.querySelector(
        '[href="https://mattamyhomes.com/privacy-policy"]'
      );

      if (element) {
        element.ariaLabel = "privacy policy (opens in a new tab)"; //fix to its visible  text label
      }

      return () => {
        if (element) {
          element.ariaLabel = "privacy policy (opens in a new tab)";
        }
      };
    }, 2000);
  }, []);

  useEffect(() => {
    if (formRef && formRef.current) {
      const current = formRef.current.children[0];
      current.setAttribute("id", "Sitecore" + (formTitle || "Form"));
    }
  }, [formRef, formTitle]);

  return (
    <>
      <FormContainer
        className={cx(
          "flex flex-col items-center justify-center lg:mb-0 mb-12"
        )}
        id={formTitle || "Form"}
        ref={formRef}
      >
        <SitecoreForm
          form={fields}
          sitecoreApiHost={String()}
          sitecoreApiKey={sitecoreApiKey}
          fieldWrapperComponent={FieldComponent}
          labelComponent={LabelComponent}
          formFetcher={formFetcher}
          onRedirect={(url) => history.push(url)}
        />
      </FormContainer>
      <WaitingScreen isOpen={isShowGreenScreen} />
    </>
  );
};

export default withRouter(Form);
